// Одно поле карточки: подпись, ввод по виду поля, подсказка, ошибка, пометки.
//
// ЗАЧЕМ ОДНИМ КОМПОНЕНТОМ НА ВСЕ ВИДЫ. Полей в карточке сделки под сорок, и половина
// «легко заполнять» — это одинаковость: обязательное всегда помечено одинаково, подсказка
// всегда под полем, ошибка всегда одна строка красным под ним же. Собранное из девяти
// отдельных компонентов это разъезжается за месяц.
//
// ЧЕТЫРЕ ПОМЕТКИ, КОТОРЫЕ ЗДЕСЬ ЖИВУТ, И ЗАЧЕМ КАЖДАЯ:
//   • «*» — обязательное. Видно ДО того, как нажали «Сохранить», а не после отказа.
//   • «изменено» — правка есть, но она ещё не сохранена. Без этой пометки человек уходит
//     со страницы, будучи уверенным, что записал.
//   • «подставлено из …» — значение пришло из связанной карточки, а не набрано. Оно
//     правится обычным образом; пометка нужна, чтобы подстановку не приняли за факт.
//   • «считает система» — поле только на чтение, и рядом сказано, кто его считает.
//     Молчаливо серое поле читается как поломка.

import { useId } from 'react'

import { fmtDate } from '../lib/format'

import { RelationPicker } from './RelationPicker'
import type { ИсточникКарточек } from './source'
import { вариантыПоля, деньги, пусто, текстЗначения, файлы, ярлык } from './values'
import { обязательно, толькоЧтение } from './validate'
import type { Значение, Значения, Поле, Сущность, Файл } from './types'

/** Валюты, которые встречаются в документах. Список короткий нарочно: длинный не читают. */
const ВАЛЮТЫ = ['RUB', 'USD', 'EUR', 'CNY']

interface Props {
  поле: Поле
  все: Значения
  onChange: (код: string, v: Значение) => void
  источник: ИсточникКарточек
  ошибка?: string | null
  /** Показывать ли ошибку: до первого касания поле не краснеет. */
  показатьОшибку?: boolean
  изменено?: boolean
  подставленоИз?: string
  путьСущности?: (с: Сущность, id: string) => string
}

/** Байты человеку. Килобайт здесь — тысяча, а не 1024: это подпись, а не расчёт. */
function размерФайла(байт: number): string {
  if (байт < 1000) return `${байт} Б`
  if (байт < 1000 * 1000) return `${Math.round(байт / 1000)} КБ`
  return `${(байт / 1000 / 1000).toFixed(1)} МБ`
}

export function Field({
  поле, все, onChange, источник, ошибка, показатьОшибку, изменено, подставленоИз, путьСущности,
}: Props) {
  const свой = useId()
  const id = `${свой}-${поле.код}`
  const idОшибки = `${id}-err`
  const значение = все[поле.код]
  const обяз = обязательно(поле, все)
  const readonly = толькоЧтение(поле, все)
  const краснеет = Boolean(ошибка) && Boolean(показатьОшибку)

  const общие = {
    id,
    disabled: readonly,
    'aria-invalid': краснеет || undefined,
    'aria-describedby': краснеет ? idОшибки : undefined,
  }

  let ввод: JSX.Element
  switch (поле.вид) {
    case 'текст':
      ввод = (
        <textarea
          {...общие}
          rows={3}
          value={пусто(значение) ? '' : String(значение)}
          maxLength={поле.максДлина}
          placeholder={поле.подсказкаВвода}
          onChange={(е) => onChange(поле.код, е.target.value)}
        />
      )
      break

    case 'число':
      ввод = (
        <input
          {...общие}
          type="text"
          inputMode="decimal"
          value={пусто(значение) ? '' : String(значение)}
          placeholder={поле.подсказкаВвода}
          onChange={(е) => {
            const s = е.target.value.trim()
            if (s === '') { onChange(поле.код, null); return }
            const n = Number(s.replace(/\s/g, '').replace(',', '.'))
            // Не число оставляем строкой: стереть набранное на лету — худшее, что можно
            // сделать с человеком, который ещё печатает. Скажет об этом проверка.
            onChange(поле.код, Number.isNaN(n) ? s : n)
          }}
        />
      )
      break

    case 'деньги': {
      const д = деньги(все, поле.код)
      ввод = (
        <span className="money-row">
          <input
            {...общие}
            type="text"
            inputMode="decimal"
            value={д.сумма === null || д.сумма === undefined ? '' : String(д.сумма)}
            placeholder={поле.подсказкаВвода}
            onChange={(е) => {
              const s = е.target.value.trim()
              const n = s === '' ? null : Number(s.replace(/\s/g, '').replace(',', '.'))
              onChange(поле.код, { сумма: n !== null && Number.isNaN(n) ? null : n, валюта: д.валюта })
            }}
          />
          <select
            id={`${id}-cur`}
            aria-label={`Валюта: ${поле.подпись}`}
            disabled={readonly}
            value={д.валюта}
            onChange={(е) => onChange(поле.код, { сумма: д.сумма, валюта: е.target.value })}
          >
            {ВАЛЮТЫ.map((в) => <option key={в} value={в}>{в}</option>)}
          </select>
        </span>
      )
      break
    }

    case 'дата':
      ввод = (
        <input
          {...общие}
          type="date"
          value={пусто(значение) ? '' : String(значение).slice(0, 10)}
          onChange={(е) => onChange(поле.код, е.target.value)}
        />
      )
      break

    case 'список':
      ввод = (
        <select
          {...общие}
          value={пусто(значение) ? '' : String(значение)}
          onChange={(е) => onChange(поле.код, е.target.value)}
        >
          <option value="">— не выбрано —</option>
          {вариантыПоля(поле, все).map((в) => (
            <option key={в.код} value={в.код}>{в.подпись}</option>
          ))}
        </select>
      )
      break

    case 'флаг':
      ввод = (
        <input
          {...общие}
          type="checkbox"
          checked={значение === true}
          onChange={(е) => onChange(поле.код, е.target.checked)}
        />
      )
      break

    case 'связь':
      ввод = (
        <RelationPicker
          сущность={поле.связьС ?? 'компания'}
          значение={ярлык(все, поле.код)}
          onChange={(v) => onChange(поле.код, v)}
          источник={источник}
          id={id}
          путь={путьСущности && поле.связьС
            ? (i: string) => путьСущности(поле.связьС as Сущность, i)
            : undefined}
          подсказкаВвода={поле.подсказкаВвода}
          disabled={readonly}
          ошибкаId={краснеет ? idОшибки : undefined}
        />
      )
      break

    case 'файл': {
      const список = файлы(все, поле.код)
      ввод = (
        <span className="files">
          {список.length === 0 && <span className="muted">Файлов нет</span>}
          {список.map((ф) => (
            <span className="file-row" key={ф.id}>
              <span className="file-name">{ф.имя}</span>
              <span className="muted">{размерФайла(ф.размер)}</span>
              {!readonly && (
                <button
                  type="button"
                  onClick={() => onChange(поле.код, список.filter((x) => x.id !== ф.id))}
                  aria-label={`Убрать файл: ${ф.имя}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!readonly && (
            <input
              id={id}
              type="file"
              multiple
              aria-label={поле.подпись}
              onChange={(е) => {
                const выбраны = Array.from(е.target.files ?? [])
                const добавка: Файл[] = выбраны.map((f, i) => ({
                  id: `новый-${Date.now()}-${i}`,
                  имя: f.name,
                  размер: f.size,
                }))
                onChange(поле.код, [...список, ...добавка])
              }}
            />
          )}
        </span>
      )
      break
    }

    default:
      ввод = (
        <input
          {...общие}
          type="text"
          value={пусто(значение) ? '' : String(значение)}
          maxLength={поле.максДлина}
          placeholder={поле.подсказкаВвода}
          onChange={(е) => onChange(поле.код, е.target.value)}
        />
      )
  }

  // Только на чтение показываем значением, а не заблокированным полем ввода: серое поле
  // ввода человек пробует нажать, и это единственное, что он из него узнаёт.
  if (readonly) {
    return (
      <div className="f card-field card-field-ro">
        <label htmlFor={id}>{поле.подпись}</label>
        <div className="ro-value" id={id}>
          {поле.вид === 'дата' ? fmtDate(пусто(значение) ? null : String(значение))
            : текстЗначения(поле, значение, все)}
        </div>
        <div className="hint">
          {поле.почемуНельзяПравить ?? 'Значение считает система, руками оно не меняется.'}
        </div>
        {поле.подсказка && <div className="hint">{поле.подсказка}</div>}
      </div>
    )
  }

  return (
    <div className={`f card-field${краснеет ? ' bad' : ''}${изменено ? ' dirty' : ''}`}>
      <label htmlFor={id}>
        {поле.подпись}
        {обяз && <span className="req" title="Обязательное поле"> *</span>}
        {изменено && <span className="badge work mark-dirty">изменено</span>}
      </label>
      {ввод}
      {подставленоИз && (
        <div className="hint">Подставлено из карточки «{подставленоИз}» — проверьте и правьте.</div>
      )}
      {поле.подсказка && <div className="hint">{поле.подсказка}</div>}
      {краснеет && <div className="error" id={idОшибки}>{ошибка}</div>}
    </div>
  )
}
